/**
 * Dashboard component for document signing statistics
 */
import * as React from "react";
import { useState, useEffect } from "react";
import "./dashboard.css";
import { proxyAvatar } from "../../_base/helper/ImageProxy";
import {
  IconCalendar,
  IconFileText,
  IconUsers,
  IconBuilding,
  IconChartPie,
  IconMail,
  IconPhone,
  IconMapPin,
  IconUser,
  IconBadge,
} from "@tabler/icons-react";
import {
  Grid,
  Select,
  Card,
  Title,
  Group,
  Text,
  Box,
  Avatar,
  Badge,
  Stack,
  Divider,
} from "@mantine/core";
import { repositorySign } from "../../_base/_const/_constVar";
import { MessageResponse } from "../../model/MessageResponse";
import { SelectListItem } from "../../model/SelectListItem";
import {
  DashbroadSearchModel,
  GroupByDepartmentModel,
  GroupByDocTypeModel,
  GroupBySetupStepCreatedate,
  GroupByStatusModel,
  PieTypeDataModel,
} from "../../model/DashbroadModel";
import { UserModel } from "../../model/UserModel";
import {
  getOrganizationsSelect,
  getUsersSelect,
} from "../../const/getSelectApi";
import { formatDateTransfer } from "../../_base/extension/FormatDate";
import Chart from "./Chart";
import { DateInput } from "@mantine/dates";
import { AgChartOptions } from "ag-charts-community";
import { AgCharts } from "ag-charts-react";

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [dataCraeteByDate, setDataCraeteByDate] = useState<
    GroupBySetupStepCreatedate[]
  >([]);
  const [dataSelectDepartment, setDataSelectDepartment] = useState<
    SelectListItem[]
  >([]);
  const [dataSelectUser, setDataSelectUser] = useState<SelectListItem[]>([]);
  const [chartFilter, setChartFilter] = useState<string>("this_month");

  const [userLogin] = useState<UserModel>(
    JSON.parse(localStorage.getItem("userLogin") || "{}")
  );

  const [search, setSearch] = useState<DashbroadSearchModel>({
    fromDate: null,
    toDate: null,
    createBy: userLogin.id ? userLogin.id : 0,
    departmentId: userLogin.departmentId ? Number(userLogin.departmentId) : 0,
  });

  const [options, setOptions] = useState<AgChartOptions>({ data: [] });
  const [options2, setOptions2] = useState<AgChartOptions>({ data: [] });
  const [options3, setOptions3] = useState<AgChartOptions>({ data: [] });

  const fetchData = async () => {
    setIsLoading(true);

    try {
      const queryParams: DashbroadSearchModel = {
        fromDate: search.fromDate,
        toDate: search.toDate,
        createBy: search.createBy,
        departmentId: search.departmentId,
      };

      const statusURL = "/api/v1/DashBoard/group-by-status";
      const docTypeURL = "/api/v1/DashBoard/group-by-doctype";
      const departmentURL = "/api/v1/DashBoard/group-by-department";
      const createByURL = "/api/v1/DashBoard/group-by-setupStep-createdate";

      const queryString = Object.entries(queryParams)
        .reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            const stringValue =
              value instanceof Date ? value.toISOString() : value.toString();
            return `${acc}&${key}=${encodeURIComponent(stringValue)}`;
          }
          return acc;
        }, "")
        .replace(/^&/, "");

      const [response1, response2, response3, response4] = await Promise.all([
        repositorySign.get<MessageResponse<GroupByStatusModel[]>>(
          `${statusURL}${queryString ? `?${queryString}` : ""}`
        ),
        repositorySign.get<MessageResponse<GroupByDocTypeModel[]>>(
          `${docTypeURL}${queryString ? `?${queryString}` : ""}`
        ),
        repositorySign.get<MessageResponse<GroupByDepartmentModel[]>>(
          `${departmentURL}${queryString ? `?${queryString}` : ""}`
        ),
        repositorySign.get<MessageResponse<GroupBySetupStepCreatedate[]>>(
          `${createByURL}${queryString ? `?${queryString}` : ""}`
        ),
      ]);

      if (response1?.success) {
        const [data1, data2, data3, data4] = [
          response1?.data,
          response2?.data,
          response3?.data,
          response4?.data,
        ];

        setDataCraeteByDate(data4 ?? []);

        // Status chart configuration
        if (data1) {
          setOptions2({
            data: data1,
            title: { text: "" },
            series: [
              {
                type: "donut",
                calloutLabelKey: "status",
                angleKey: "quantity",
                innerRadiusRatio: 0.6,
                calloutLabel: { enabled: false },
                innerLabels: [
                  { text: "Tổng hồ sơ", fontWeight: "bold", fontSize: 12 },
                  {
                    text: data1
                      .reduce((total, item) => total + item.quantity, 0)
                      .toString(),
                    spacing: 4,
                    fontSize: 18,
                    fontWeight: "bold",
                  },
                ],
              },
            ],
          });
        }

        // Document type chart configuration
        if (data2) {
          setOptions({
            data: data2,
            title: { text: "" },
            series: [
              {
                type: "donut",
                calloutLabelKey: "docTypeName",
                angleKey: "quantity",
                innerRadiusRatio: 0.6,
                calloutLabel: { enabled: false },
                innerLabels: [
                  { text: "Tổng hồ sơ", fontWeight: "bold", fontSize: 12 },
                  {
                    text: data2
                      .reduce((total, item) => total + item.quantity, 0)
                      .toString(),
                    spacing: 4,
                    fontSize: 18,
                    fontWeight: "bold",
                  },
                ],
              },
            ],
          });
        }

        // Department chart configuration
        if (data3) {
          setOptions3({
            data: data3,
            title: { text: "" },
            series: [
              {
                type: "donut",
                angleKey: "quantity",
                innerRadiusRatio: 0.6,
                calloutLabel: { enabled: false },
                innerLabels: [
                  { text: "Tổng hồ sơ", fontWeight: "bold", fontSize: 12 },
                  {
                    text: data3
                      .reduce((total, item) => total + item.quantity, 0)
                      .toString(),
                    spacing: 4,
                    fontSize: 18,
                    fontWeight: "bold",
                  },
                ],
              },
            ],
          });
        }
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredChartData: PieTypeDataModel[] = React.useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Lấy ngày bắt đầu và kết thúc của tuần hiện tại (Thứ 2 đến Chủ Nhật)
    const currentDay = now.getDay() || 7;
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - currentDay + 1);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return dataCraeteByDate
      .filter((item) => {
        if (chartFilter === "all") return true;

        const itemDate = new Date(item.createdate);

        if (chartFilter === "this_month") {
          return (
            itemDate.getMonth() === currentMonth &&
            itemDate.getFullYear() === currentYear
          );
        }
        if (chartFilter === "this_year") {
          return itemDate.getFullYear() === currentYear;
        }
        if (chartFilter === "this_week") {
          return itemDate >= startOfWeek && itemDate <= endOfWeek;
        }
        if (chartFilter === "today") {
          return (
            itemDate.getDate() === now.getDate() &&
            itemDate.getMonth() === currentMonth &&
            itemDate.getFullYear() === currentYear
          );
        }
        return true;
      })
      .map((item) => {
        const formattedDate = formatDateTransfer(item.createdate).split(" ")[0];
        return {
          x: formattedDate,
          y: item.quantity,
          text: formattedDate,
        };
      });
  }, [dataCraeteByDate, chartFilter]);

  const fetchDataDepartment = async () => {
    const dataApi = await getOrganizationsSelect();
    if (dataApi?.length > 0) {
      const filteredData = dataApi.filter((item: any) => item.text !== null);
      setDataSelectDepartment(filteredData);
    }
  };

  const fetchDataUser = async () => {
    const dep = search.departmentId;
    const dataApi = await getUsersSelect(dep);
    if (dataApi?.length > 0) {
      const filteredData = dataApi.filter((item: any) => item.text !== null);
      setDataSelectUser(filteredData);
    }
  };

  useEffect(() => {
    fetchData();
  }, [search]);

  useEffect(() => {
    // Reset createBy when department changes and fetch new users
    setSearch(prevSearch => ({
      ...prevSearch,
      createBy: 0,
    }));
    fetchDataUser();
  }, [search.departmentId]);

  useEffect(() => {
    fetchDataDepartment();
    fetchDataUser();
  }, []);

  return (
    <Box p={{ base: "xs", sm: "sm", md: "md" }}>
      {/* Enhanced Header Section with User Info */}
      <Grid mb={{ base: "xs", sm: "sm" }}>
        <Grid.Col span={{ base: 12, md: 12, lg: 8 }}>
          <Card shadow="sm" p={{ base: "sm", md: "md" }} radius="md" withBorder>
            <Group justify="space-between" mb="sm">
              <Title order={2} c="blue.7">
                📊 Dashboard Thống Kê
              </Title>
            </Group>

            {/* Filter Section */}
            <Grid>
              <Grid.Col span={{ base: 12, xs: 6, sm: 6, md: 3 }}>
                <Select
                  label="Phòng ban"
                  placeholder="Chọn phòng ban"
                  leftSection={<IconBuilding size={16} />}
                  clearable
                  searchable
                  size="sm"
                  data={dataSelectDepartment.map((item) => ({
                    label: item.text ?? "",
                    value: String(item.value ?? ""),
                  }))}
                  onChange={(value) => {
                    setSearch({
                      ...search,
                      departmentId: Number(value) ?? 0,
                    });
                  }}
                  value={String(search.departmentId)}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, xs: 6, sm: 6, md: 3 }}>
                <Select
                  label="Người tạo"
                  placeholder="Chọn người tạo"
                  leftSection={<IconUsers size={16} />}
                  clearable
                  searchable
                  size="sm"
                  data={dataSelectUser.map((item) => ({
                    label: item.text ? item.text.split("-")[0].trim() : "",
                    value: String(item.value ?? ""),
                  }))}
                  value={search.createBy === 0 ? null : String(search.createBy)}
                  onChange={(value) => {
                    setSearch({
                      ...search,
                      createBy: Number(value) ?? 0,
                    });
                  }}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, xs: 6, sm: 6, md: 3 }}>
                <DateInput
                  label="Từ ngày"
                  placeholder="Chọn từ ngày"
                  leftSection={<IconCalendar size={16} />}
                  valueFormat="DD/MM/YYYY"
                  clearable
                  size="sm"
                  onChange={(e) => setSearch({ ...search, fromDate: e })}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, xs: 6, sm: 6, md: 3 }}>
                <DateInput
                  label="Đến ngày"
                  placeholder="Chọn đến ngày"
                  leftSection={<IconCalendar size={16} />}
                  valueFormat="DD/MM/YYYY"
                  clearable
                  size="sm"
                  onChange={(e) => setSearch({ ...search, toDate: e })}
                />
              </Grid.Col>
            </Grid>
          </Card>
        </Grid.Col>

        {/* Modern User Information Card */}
        <Grid.Col span={{ base: 12, md: 12, lg: 4 }}>
          <Card
            shadow="sm"
            p="xs"
            radius="md"
            withBorder
            h="100%"
            style={{
              background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
              border: "1px solid #dee2e6",
            }}
          >
            <Stack gap="xs" h="100%" justify="space-between">
              <Group>
                <Avatar
                  src={proxyAvatar(userLogin.avatar, 32)}
                  size={32}
                  radius="xl"
                  style={{ border: "1px solid #495057" }}
                  color="blue"
                >
                  <IconUser size={16} />
                </Avatar>
                <Stack gap={1} style={{ flex: 1 }}>
                  <Text size="xs" fw={600} c="dark.8">
                    Xin chào, {userLogin.name || "Người dùng"}!
                  </Text>
                  <Text size="xs" c="dimmed">
                    @{userLogin.userName || "username"}
                  </Text>
                  <Text size="xs" c="dark.6">
                    ID: {userLogin.id}
                  </Text>
                </Stack>
              </Group>

              <Divider color="gray.3" />

              <Stack gap="xs">
                <Group gap="xs">
                  <IconCalendar size={10} color="var(--mantine-color-gray-6)" />
                  <Text size="xs" c="dark.6">
                    {new Date().toLocaleString("vi-VN", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Text>
                </Group>

                {userLogin.phone && (
                  <Group gap="xs">
                    <IconPhone size={10} color="var(--mantine-color-gray-6)" />
                    <Text size="xs" c="dark.6">
                      {userLogin.phone}
                    </Text>
                  </Group>
                )}
              </Stack>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Charts Section */}
      <Grid gutter={{ base: "xs", sm: "sm" }}>
        <Grid.Col span={{ base: 12, sm: 6, md: 6, lg: 4 }}>
          <Card
            shadow="sm"
            p={{ base: "sm", md: "md" }}
            radius="md"
            withBorder
            h={{ base: 350, sm: 400 }}
          >
            <Group mb="xs">
              <IconFileText size={18} color="var(--mantine-color-blue-6)" />
              <Title order={5}>Theo loại văn bản</Title>
            </Group>
            <AgCharts style={{ height: "280px" }} options={options} />
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 6, lg: 4 }}>
          <Card
            shadow="sm"
            p={{ base: "sm", md: "md" }}
            radius="md"
            withBorder
            h={{ base: 350, sm: 400 }}
          >
            <Group mb="xs">
              <IconChartPie size={18} color="var(--mantine-color-green-6)" />
              <Title order={5}>Theo trạng thái</Title>
            </Group>
            <AgCharts style={{ height: "280px" }} options={options2} />
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 12, md: 12, lg: 4 }}>
          <Card
            shadow="sm"
            p={{ base: "sm", md: "md" }}
            radius="md"
            withBorder
            h={{ base: 350, sm: 400 }}
          >
            <Group mb="xs">
              <IconBuilding size={18} color="var(--mantine-color-orange-6)" />
              <Title order={5}>Theo tổ chức</Title>
            </Group>
            <AgCharts style={{ height: "280px" }} options={options3} />
          </Card>
        </Grid.Col>

        <Grid.Col span={12}>
          <Card shadow="sm" p={{ base: "sm", md: "md" }} radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Group gap="xs">
                <IconCalendar size={18} color="var(--mantine-color-violet-6)" />
                <Title order={5}>Biểu đồ theo thời gian</Title>
              </Group>
              <Select
                size="xs"
                w={130}
                value={chartFilter}
                onChange={(val) => setChartFilter(val || "this_month")}
                data={[
                  { label: "Hôm nay", value: "today" },
                  { label: "Tuần này", value: "this_week" },
                  { label: "Tháng này", value: "this_month" },
                  { label: "Năm nay", value: "this_year" },
                  { label: "Tất cả", value: "all" },
                ]}
              />
            </Group>
            <Chart data={filteredChartData} />
          </Card>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export default Dashboard;
